import React, { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useNextStep } from 'nextstepjs'

import { useAppSelector } from '#/app/store/store'
import { useFeatureFlag } from '#/shared/lib/hooks/use-feature-flag'
import { getDeviceType } from '#/shared/lib/helpers'
import { TourName, getTourSteps } from '../lib/constants'
import { setTourCompleted, isTourCompleted } from '../lib/tour-storage'
import { TourSelector } from './tour-selector'
import { TourCompletionModal } from './tour-completion-modal'

interface TourManagerProps {
	children: React.ReactNode
}

export const TourManager: React.FC<TourManagerProps> = ({ children }) => {
	const { startNextStep, setCurrentStep, currentTour, currentStep, isNextStepVisible, closeNextStep } = useNextStep()
	const { data: session } = useSession()
	const isOnboardingEnabled = useFeatureFlag('onboarding-enabled')
	const [showTourSelector, setShowTourSelector] = useState(false)
	const [showCompletionModal, setShowCompletionModal] = useState(false)
	const [pendingTour, setPendingTour] = useState<TourName | null>(null)
	const prevTourStateRef = useRef<{ tour: string | null; visible: boolean }>({ tour: null, visible: false })
	const hasAdvancedStep = useRef(false)
	const currentStepRef = useRef(currentStep)
	const router = useRouter()

	useEffect(() => {
		currentStepRef.current = currentStep
	}, [currentStep])

	const isTourSelectorShown = (): boolean => {
		if (typeof window === 'undefined') return false
		return sessionStorage.getItem('tour-selector-shown') === 'true'
	}

	
	const setTourSelectorShown = (): void => {
		if (typeof window !== 'undefined') {
			sessionStorage.setItem('tour-selector-shown', 'true')
		}
	}

	const handleTourSelect = (tourName: TourName) => {
		setShowTourSelector(false)

		if (tourName === 'text-models') {
			// Переходим на страницу с текстовыми моделями
			setPendingTour(tourName)
			router.push('/chat-bot')
		} else if (tourName === 'images') {
			// Переходим на страницу с изображениями
			setPendingTour(tourName)
			router.push('/images')
		}
	}

	const handleSkip = () => {
		setTourCompleted()
		setShowTourSelector(false)
		setTourSelectorShown()
	}

	// Обработчик завершения тура на основе хуков nextstepjs
	useEffect(() => {
		const wasVisibleWithTour = prevTourStateRef.current.visible && prevTourStateRef.current.tour
		const isNowClosed = !isNextStepVisible && !currentTour

		if (wasVisibleWithTour && isNowClosed) {
			const tourSteps = getTourSteps(getDeviceType())
			const tourConfig = tourSteps.find((t) => t.tour === prevTourStateRef.current.tour)
			const totalSteps = tourConfig?.steps.length ?? 0
			if (currentStep === totalSteps - 1) {
				setShowCompletionModal(true)
			}
		}

		prevTourStateRef.current = { tour: currentTour, visible: isNextStepVisible }
	}, [currentTour, isNextStepVisible, currentStep])

	const handleCompletionModalClose = () => {
		setTourCompleted()
		setShowCompletionModal(false)
		setTourSelectorShown()
	}

	useEffect(() => {
		const handleUserSentMessage = () => {
			if (currentTour) {
				closeNextStep()
				setShowCompletionModal(true)
			}
		}
		window.addEventListener('user-sent-message', handleUserSentMessage)
		return () => window.removeEventListener('user-sent-message', handleUserSentMessage)
	}, [currentTour, closeNextStep])

	useEffect(() => {
		// Проверяем feature flag: tour-manager работает только если onboarding-enabled включен
		if (!isOnboardingEnabled) {
			return
		}

		if (pendingTour === 'text-models' && router.pathname === '/chat-bot') {
			const timer = setTimeout(() => {
				startNextStep(pendingTour)
				setPendingTour(null)
			}, 500)

			return () => clearTimeout(timer)
		}

		if (pendingTour === 'images' && router.pathname === '/images') {
			const timer = setTimeout(() => {
				startNextStep(pendingTour)
				setPendingTour(null)
			}, 500)

			return () => clearTimeout(timer)
		}

	if (router.pathname === '/chat-bot/[slug]' || router.pathname === '/images/[slug]') {
		if (!hasAdvancedStep.current && currentTour) {
			const timer = setTimeout(() => {
				setCurrentStep(1)
				hasAdvancedStep.current = true
			}, 500)

			return () => clearTimeout(timer)
		}
	} else {
		hasAdvancedStep.current = false
	}

	}, [router.pathname, pendingTour, startNextStep, currentTour, setCurrentStep, isOnboardingEnabled])

	const created_at = useAppSelector((state) => state.user.created_at)

	const isNewUser = (): boolean => {
		if (!created_at) return false

		const createdDate = new Date(created_at)
		const now = new Date()
		const minutesSinceCreation = (now.getTime() - createdDate.getTime()) / (1000 * 60)

		// Пользователь считается новым, если зарегистрирован менее 60 минут назад
		return minutesSinceCreation < 60
	}

	useEffect(() => {
		// Проверяем feature flag: tour-manager работает только если onboarding-enabled включен
		if (!isOnboardingEnabled) {
			return
		}

		if (session?.user && session?.access) {
			// Показываем TourSelector только если:
			// 1. Тур еще не завершен
			// 2. TourSelector еще не был показан в текущей сессии
			// 3. Пользователь зарегшистрировался недавно
			if (isNewUser() && !isTourSelectorShown() && !isTourCompleted()) {
				setTimeout(() => {
					setShowTourSelector(true)
					setTourSelectorShown()
				}, 1000)
			}
		}
	}, [session, isOnboardingEnabled])

	return (
		<>
			<TourSelector open={showTourSelector} onSelect={handleTourSelect} onSkip={handleSkip} />
			<TourCompletionModal open={showCompletionModal} onClose={handleCompletionModalClose} />
			{children}
		</>
	)
}

